import { motion } from "framer-motion";
import { UtensilsCrossed, Beer, CalendarDays, Star, Sparkles } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Reveal } from "@/components/site/Reveal";
import {
  FOOD_MENU,
  DRINK_MENU,
  DAILY_SPECIALS,
  HAPPY_HOUR,
  ENTERTAINMENT,
} from "@/data/menu";

const fade = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

function ItemRow({ name, price, desc }) {
  return (
    <div className="py-3">
      <div className="flex items-baseline gap-3">
        <span className="font-display text-lg md:text-xl text-[#F8F5F2] leading-tight">
          {name}
        </span>
        {price ? (
          <>
            <span className="flex-1 border-b border-dotted border-white/25 mb-1.5" />
            <span className="font-label text-sm text-primary tracking-wider whitespace-nowrap">
              {price}
            </span>
          </>
        ) : null}
      </div>
      {desc && (
        <p className="mt-1.5 text-sm text-[#8E94A3] leading-relaxed">{desc}</p>
      )}
    </div>
  );
}

function GridItem({ name, price }) {
  return (
    <div className="rounded-md border border-white/10 bg-[#0B132B] px-3 py-2 flex items-baseline justify-between gap-2 hover:border-primary/40 transition-colors">
      <span className="font-label text-[11px] text-[#F8F5F2]/85 tracking-wider">
        {name}
      </span>
      {price && (
        <span className="font-label text-[11px] text-primary">{price}</span>
      )}
    </div>
  );
}

function Section({ section, note, items, layout, footer }) {
  return (
    <div className="mb-10 break-inside-avoid">
      <div className="mb-3 flex items-center gap-3">
        <span className="h-px flex-1 bg-white/10" />
        <h3 className="font-display text-xl md:text-2xl text-primary uppercase tracking-widest">
          {section}
        </h3>
        <span className="h-px flex-1 bg-white/10" />
      </div>
      {note && (
        <p className="mb-4 text-center text-xs text-[#F8F5F2]/70 italic">
          {note}
        </p>
      )}
      {layout === "grid" ? (
        <div className="grid grid-cols-2 gap-2">
          {items.map((it) => (
            <GridItem key={it.name} {...it} />
          ))}
        </div>
      ) : (
        <div>
          {items.map((it) => (
            <ItemRow key={it.name} {...it} />
          ))}
        </div>
      )}
      {footer && (
        <p className="mt-3 text-xs text-[#F8F5F2]/50 italic">{footer}</p>
      )}
    </div>
  );
}

function DaySpecial({ special, index }) {
  const isSunday = special.day === "Sunday";
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className={`rounded-md border border-white/10 bg-[#0B132B] p-6 md:p-8 ${
        isSunday ? "md:col-span-2" : ""
      }`}
    >
      <div className="mb-1 flex items-baseline gap-3">
        <span className="font-label text-[11px] text-primary tracking-[0.25em]">
          {special.day}
        </span>
      </div>
      <h4 className="font-display text-2xl md:text-3xl text-[#F8F5F2]">
        {special.title}
      </h4>
      {special.note && (
        <p className="mt-2 text-xs text-[#F8F5F2]/60 leading-relaxed">
          {special.note}
        </p>
      )}
      <div className="mt-5 border-t border-white/10 pt-3">
        {special.items.map((it) => (
          <ItemRow key={it.name} {...it} />
        ))}
      </div>
      {special.aLaCarte && (
        <div className="mt-6 border-t border-white/10 pt-4">
          <p className="mb-1 font-label text-[10px] text-primary tracking-[0.22em]">
            À La Carte
          </p>
          {special.aLaCarte.map((it) => (
            <ItemRow key={it.name} {...it} />
          ))}
        </div>
      )}
      {special.cocktails && (
        <div className="mt-6 border-t border-white/10 pt-4">
          <p className="mb-1 font-label text-[10px] text-primary tracking-[0.22em]">
            Sunday Cocktails
          </p>
          {special.cocktails.map((it) => (
            <ItemRow key={it.name} {...it} />
          ))}
        </div>
      )}
      {special.footer && (
        <p className="mt-4 text-xs text-[#F8F5F2]/50 italic">
          {special.footer}
        </p>
      )}
    </motion.div>
  );
}

const TABS = [
  { v: "food", Icon: UtensilsCrossed, label: "Food" },
  { v: "drinks", Icon: Beer, label: "Drinks" },
  { v: "specials", Icon: CalendarDays, label: "Daily Specials" },
];

export default function Menu() {
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-[#1C2541] py-16 md:py-24 overflow-hidden"
    >
      {/* subtle glow */}
      <div className="absolute inset-0 pointer-events-none opacity-60">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-primary/10 blur-[140px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-14 text-center">
          <Reveal>
            <span className="font-label text-xs text-primary tracking-[0.28em]">
              The Menu
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display text-5xl md:text-7xl text-[#F8F5F2]">
              What we're pouring <br className="hidden md:block" />
              <span className="text-primary">& plating.</span>
            </h2>
          </Reveal>
        </div>

        <Tabs defaultValue="food" className="w-full">
          <div className="flex justify-center mb-12">
            <TabsList
              data-testid="menu-tabs"
              className="bg-[#0B132B] border border-white/10 p-1 h-auto rounded-full flex-wrap"
            >
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.v}
                  value={t.v}
                  data-testid={`menu-tab-${t.v}`}
                  className="rounded-full px-5 md:px-7 py-2.5 font-label text-xs text-[#F8F5F2]/70 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none transition-all"
                >
                  <t.Icon className="h-4 w-4 mr-2" /> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="food" data-testid="menu-panel-food">
            <motion.div
              key="food"
              {...fade}
              className="columns-1 md:columns-2 gap-x-12"
            >
              {FOOD_MENU.map((s) => (
                <Section key={s.section} {...s} />
              ))}
            </motion.div>
          </TabsContent>

          <TabsContent value="drinks" data-testid="menu-panel-drinks">
            <motion.div key="drinks" {...fade}>
              <div className="mb-10 rounded-md border border-primary/40 bg-primary/5 p-6 md:p-8 text-center">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Star className="h-4 w-4 text-primary fill-primary" />
                  <span className="font-label text-xs text-primary tracking-[0.28em]">
                    {HAPPY_HOUR.title}
                  </span>
                  <Star className="h-4 w-4 text-primary fill-primary" />
                </div>
                <p className="font-display text-2xl md:text-3xl text-[#F8F5F2]">
                  {HAPPY_HOUR.hours}
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {HAPPY_HOUR.deals.map((d) => (
                    <span
                      key={d}
                      className="rounded-full border border-primary/40 bg-[#0B132B] px-4 py-1.5 font-label text-[11px] text-primary"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
              <div className="columns-1 md:columns-2 gap-x-12">
                {DRINK_MENU.map((s) => (
                  <Section key={s.section} {...s} />
                ))}
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="specials" data-testid="menu-panel-specials">
            <motion.div key="specials" {...fade}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DAILY_SPECIALS.map((s, i) => (
                  <DaySpecial key={s.day} special={s} index={i} />
                ))}
              </div>

              <div className="mt-6 rounded-md border border-white/10 bg-[#0B132B] p-6 md:p-8">
                <div className="mb-5 flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/15 text-primary">
                    <CalendarDays className="h-5 w-5" />
                  </span>
                  <h4 className="font-display text-2xl md:text-3xl text-[#F8F5F2]">
                    Weekly Entertainment
                  </h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {ENTERTAINMENT.map((e) => (
                    <div
                      key={e.day}
                      className="rounded-md border border-white/10 bg-[#1C2541] px-4 py-3"
                    >
                      <div className="font-label text-[10px] text-primary tracking-[0.22em]">
                        {e.day}
                      </div>
                      <div className="mt-1.5 font-display text-lg text-[#F8F5F2] leading-tight">
                        {e.event}
                      </div>
                      {e.tag && (
                        <div className="text-[11px] text-[#F8F5F2]/50 mt-1">
                          {e.tag}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
